// listener for vnc tab closed messages and vnc connection failures

import { postVPNAuthKeySupportSessionDto } from "@src/cloud-connect/api/vpn-manager/vpn-post-generate-auth-key-support-session";
import { vncWebsocketUrlEnvVar } from "@src/cloud-connect/feature-toggles/feature-toggles";
import { postVPNAuthKeySupportSessionUseCase } from "@src/cloud-connect/vpn-manager/use-cases/vpn-post-generate-auth-key-support-session-use-case";
import {
  clearVncKey,
  getVncNamespace,
  hasPendingVncHandshake,
  isExpectedVncTab,
  markVncTabReady,
  sendVncKey,
} from "@src/cloud-connect/vnc/utils/open-vnc";
import { useEffect } from "react";

const RETRY_TIMEOUT_MS = 2000; // time to wait before the first auth key fetch after CONNECTION_FAILED
const AUTH_KEY_RETRY_TIMEOUT_MS = 500; // time to wait between auth key fetch retries
const AUTH_KEY_MAX_RETRIES = 3; // max auth key fetch attempts per CONNECTION_FAILED cycle

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// child can send vnc tab ready more than once for the same session
// only one fetch may run at a time
const vncKeyFetchInProgress = new Set<string>();
export const useHandleVncMessages = () => {
  useEffect(() => {
    const retryTimers = new Set<number>();

    // fetch a fresh auth key and retain it only until the child confirms
    // its rfb connection. covers refresh before consume
    const fetchAndSendAuthKey = async (
      namespace: string,
      sessionId: string,
      source: MessageEventSource,
      origin: string,
      messageType: "VNC_KEY" | "VNC_RETRY" = "VNC_RETRY",
    ): Promise<void> => {
      if (!isExpectedVncTab(sessionId, source)) return;

      const dto: postVPNAuthKeySupportSessionDto = {
        namespace,
        id: sessionId,
      };
      let websocketAuthKey: string | undefined;

      if (vncKeyFetchInProgress.has(sessionId)) {
        console.warn(
          `[Parent] Auth key fetch already in progress for session ${sessionId}, dropping request`,
        );
        return;
      }
      vncKeyFetchInProgress.add(sessionId);

      try {
        for (let attempt = 0; attempt < AUTH_KEY_MAX_RETRIES; attempt++) {
          try {
            const response = await postVPNAuthKeySupportSessionUseCase(dto);
            websocketAuthKey = response.data?.websocket_auth_key ?? undefined;

            if (websocketAuthKey) break;

            console.warn(
              `[Parent] Auth key not yet available (attempt ${
                attempt + 1
              }/${AUTH_KEY_MAX_RETRIES}), retrying in ${AUTH_KEY_RETRY_TIMEOUT_MS}ms...`,
              response.error,
            );
          } catch (error) {
            console.error(
              `[Parent] Error fetching auth key (attempt ${
                attempt + 1
              }/${AUTH_KEY_MAX_RETRIES}), retrying in ${AUTH_KEY_RETRY_TIMEOUT_MS}ms...`,
              error,
            );
          }

          if (attempt + 1 < AUTH_KEY_MAX_RETRIES) {
            await sleep(AUTH_KEY_RETRY_TIMEOUT_MS);
          }
        }

        // do not send a result to a tab that has since been replaced
        if (!isExpectedVncTab(sessionId, source)) return;

        if (!websocketAuthKey) {
          console.warn(
            `[Parent] All auth key fetch attempts failed for session ${sessionId} — sending VNC_SESSION_ENDED`,
          );
          clearVncKey(sessionId, source);
          (source as Window).postMessage(
            { type: "VNC_SESSION_ENDED", sessionId },
            origin,
          );
          return;
        }

        const wsUrl = `${vncWebsocketUrlEnvVar()}/api/v1/vnc/${websocketAuthKey}`;
        sendVncKey(source as Window, sessionId, wsUrl, messageType);
      } finally {
        vncKeyFetchInProgress.delete(sessionId);
      }
    };

    const handleVncMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      const { type, sessionId, readyId } = event.data || {};
      const source = event.source;

      if (
        type === "VNC_TAB_READY" &&
        sessionId &&
        source &&
        typeof readyId === "string"
      ) {
        // initial delivery is handled by open-vnc.ts
        // after delivery accept only a new page load id from the tab opened for this session
        if (
          !isExpectedVncTab(sessionId, source) ||
          hasPendingVncHandshake(sessionId) ||
          !markVncTabReady(sessionId, source, readyId)
        ) {
          return;
        }

        const namespace = getVncNamespace(sessionId);
        if (!namespace || vncKeyFetchInProgress.has(sessionId)) return;

        console.log(
          "[Parent] VNC tab ready without pending handshake — fetching fresh auth key for session:",
          sessionId,
        );
        void fetchAndSendAuthKey(
          namespace,
          sessionId,
          source,
          event.origin,
          "VNC_KEY",
        );
        return;
      }

      if (type === "VNC_CLOSED" && sessionId) {
        console.log("[Parent] VNC tab closed for session:", sessionId);
      }

      if (type === "CONNECTION_FAILED" && sessionId && source) {
        const namespace = getVncNamespace(sessionId);
        if (!namespace || !isExpectedVncTab(sessionId, source)) return;

        console.log("[Parent] VNC connection failed for session:", sessionId);
        console.log("[Parent] Requesting new auth key for retry...");
        clearVncKey(sessionId, source);

        const timer = window.setTimeout(() => {
          retryTimers.delete(timer);
          void fetchAndSendAuthKey(
            namespace,
            sessionId,
            source,
            event.origin,
            "VNC_RETRY",
          );
        }, RETRY_TIMEOUT_MS);
        retryTimers.add(timer);
      }
    };

    window.addEventListener("message", handleVncMessage);

    return () => {
      window.removeEventListener("message", handleVncMessage);
      retryTimers.forEach((timer) => window.clearTimeout(timer));
    };
  }, []);
};
